import type { AdaptivePlayerProfile, Difficulty } from './types.js';

export interface DifficultyProfile {
  id: Difficulty;
  biddingMinimumSamples: number;
  playingMinimumWorlds: number;
  playingMaximumWorlds: number;
  targetThinkMs: number;
  exactEndgameAtTiles: number | null;
  nearEqualMoveTemperature: number;
  useMatchEquity: boolean;
  useBehaviorWeights: boolean;
}

/** Serializable snapshot sent to the AI worker — immutable for a match. */
export interface SerializableDifficultyProfile {
  id: Difficulty;
  biddingMinimumSamples: number;
  playingMinimumWorlds: number;
  playingMaximumWorlds: number;
  targetThinkMs: number;
  exactEndgameAtTiles: number | null;
  nearEqualMoveTemperature: number;
  useMatchEquity: boolean;
  useBehaviorWeights: boolean;
}

export interface SerializableAdaptiveProfileSnapshot {
  completedHands: number;
  confidence: number;
  behaviorModel?: import('./types.js').AdaptiveBehaviorModelSnapshot;
}

export interface ProductSerializableAIConfig {
  aiRngSeed: number;
  profile: SerializableDifficultyProfile;
  adaptiveProfile?: SerializableAdaptiveProfileSnapshot;
}

export const DIFFICULTY_PROFILES: Record<Difficulty, DifficultyProfile> = {
  casual: {
    id: 'casual',
    biddingMinimumSamples: 5,
    playingMinimumWorlds: 4,
    playingMaximumWorlds: 4,
    targetThinkMs: 800,
    exactEndgameAtTiles: null,
    nearEqualMoveTemperature: 0.18,
    useMatchEquity: false,
    useBehaviorWeights: false,
  },
  competitive: {
    id: 'competitive',
    biddingMinimumSamples: 5,
    playingMinimumWorlds: 10,
    playingMaximumWorlds: 10,
    targetThinkMs: 2_000,
    exactEndgameAtTiles: null,
    nearEqualMoveTemperature: 0.06,
    useMatchEquity: false,
    useBehaviorWeights: false,
  },
  expert: {
    id: 'expert',
    biddingMinimumSamples: 8,
    playingMinimumWorlds: 16,
    playingMaximumWorlds: 128,
    targetThinkMs: 3_500,
    exactEndgameAtTiles: 8,
    nearEqualMoveTemperature: 0.01,
    useMatchEquity: true,
    useBehaviorWeights: false,
  },
  grandad: {
    id: 'grandad',
    biddingMinimumSamples: 8,
    playingMinimumWorlds: 16,
    playingMaximumWorlds: 128,
    targetThinkMs: 3_500,
    exactEndgameAtTiles: 8,
    nearEqualMoveTemperature: 0.01,
    useMatchEquity: true,
    useBehaviorWeights: true,
  },
};

export function getDifficultyProfile(
  difficulty: Difficulty,
): DifficultyProfile {
  return { ...DIFFICULTY_PROFILES[difficulty] };
}

export function toSerializableDifficultyProfile(
  profile: DifficultyProfile,
): SerializableDifficultyProfile {
  return { ...profile };
}

export function toSerializableAIConfig(
  difficulty: Difficulty,
  aiRngSeed: number,
  adaptiveProfile?: AdaptivePlayerProfile,
): ProductSerializableAIConfig {
  const profile = getDifficultyProfile(difficulty);
  const config: ProductSerializableAIConfig = {
    aiRngSeed,
    profile: toSerializableDifficultyProfile(profile),
  };

  if (difficulty === 'grandad' && adaptiveProfile) {
    config.adaptiveProfile = {
      completedHands: adaptiveProfile.completedHands,
      confidence: adaptiveProfile.confidence,
      ...(adaptiveProfile.behaviorModel
        ? { behaviorModel: adaptiveProfile.behaviorModel }
        : {}),
    };
  }

  return config;
}

/** Grandad with no evidence behaves like Expert (Phase 15 fills in personalization). */
export function effectiveGrandadProfile(
  profile: SerializableDifficultyProfile,
  adaptiveProfile?: SerializableAdaptiveProfileSnapshot,
): SerializableDifficultyProfile {
  if (profile.id !== 'grandad') return profile;

  const hasEvidence =
    adaptiveProfile !== undefined &&
    adaptiveProfile.completedHands > 0 &&
    adaptiveProfile.confidence > 0;

  if (hasEvidence) {
    return profile;
  }

  return {
    ...getDifficultyProfile('expert'),
    id: 'grandad',
    useBehaviorWeights: false,
  };
}
